import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';

import { useRiskScore } from '@/hooks/useRiskScore';

import { useControlTypeLabel } from '../../../../assessments/forms/useControlTypeLabel';
import { UNRATED } from '../../../../controls/lookupData';
import type {
  RiskAssessmentResultFlatFields,
  RiskAssessmentResultRegisterFields,
} from './types';

export const useRiskRatingLabelledFields = (
  riskId: string,
  records: RiskAssessmentResultFlatFields[] | undefined
) => {
  const statusRating = useRating('assessment_status');
  const getControlTypeLabel = useControlTypeLabel();
  const uncontrolledRiskRating = useRating('risk_uncontrolled');
  const controlledRiskRating = useRating('risk_controlled');
  const likelihoodRating = useRating('likelihood');
  const impactRating = useRating('impact');
  const riskScores = useRiskScore(riskId);

  return useMemo<RiskAssessmentResultRegisterFields[]>(() => {
    return (
      records?.map((d) => {
        const assessment = d.parents.find((p) => p.assessment)?.assessment;

        return {
          ...d,
          StatusLabelled:
            assessment !== undefined
              ? (statusRating.getLabel(assessment?.Status) ?? '-"')
              : '-;',
          NextTestDate: assessment?.NextTestDate ?? '-',
          ActualCompletionDate: assessment?.ActualCompletionDate ?? '-',
          ParentTitle: assessment?.Title ?? '-',
          ControlTypeLabelled:
            getControlTypeLabel(d.ControlType) || UNRATED.label,
          CompletionDate: assessment?.ActualCompletionDate ?? '-',
          RatingLabelled: !riskScores.showScore
            ? d.ControlType ===
              Risk_Assessment_Result_Control_Type_Enum.Controlled
              ? controlledRiskRating.getLabel(d.Rating)
              : (uncontrolledRiskRating.getLabel(d.Rating) ?? UNRATED.label)
            : '-',
          ImpactLabelled: impactRating.getLabel(d.Impact) ?? UNRATED.label,
          LikelihoodLabelled:
            likelihoodRating.getLabel(d.Likelihood) ?? UNRATED.label,
        };
      }) || []
    );
  }, [
    records,
    statusRating,
    getControlTypeLabel,
    controlledRiskRating,
    uncontrolledRiskRating,
    impactRating,
    likelihoodRating,
    riskScores,
  ]);
};
